import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "How to write tests for XState – CodeWithSwiz 12",
  "description": "Once you know how to refactor a useReducer to XState, you gotta prove you did it right. ",
  "published": "2020-10-13T00:00:00.000Z",
  "categories": "CodeWithSwiz, Livecoding, Technical, XState",
  "hero": "./img/screenshot-1602604132265.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Once you know `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/refactoring-a-usereducer-to-xstate-pt1-codewithswiz-11/"
      }}>{`how to refactor a useReducer to XState`}</a>{`, you gotta prove you did it right.`}</p>
    <p><em parentName="p">{`CodeWithSwiz is a twice-a-week live show. Like a podcast with video and fun hacking. Focused on experiments. `}<a parentName="em" {...{
          "href": "https://youtube.com/swizecteller"
        }}>{`Join live Wednesdays and Sundays`}</a></em></p>
    <lite-youtube {...{
      "videoid": "U0yHKAuTG98",
      "videostartat": "0"
    }}></lite-youtube>
    <p><a parentName="p" {...{
        "href": "https://github.com/Swizec/useAuth"
      }}>{`useAuth`}</a>{` had existing tests for its core reducer and that makes refactoring easier.`}</p>
    <p>{`In theory tests are great for refactoring. You can change the implementation, leave tests alone, get them passing. Verify you refactored without destroying the external API.`}</p>
    <p>{`My tests were not that good.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`// src/__tests__/authReducer.spec.ts
describe("login", () => {
    const currentTime = new Date().getTime();
    const state: AuthState = { // ... };
    const action: AuthAction = { // ... };

    it("adds user to local storage", () => {
        localStorage.removeItem("useAuth:expires_at");
        localStorage.removeItem("useAuth:user");

        authReducer(state, action);

        expect(
            JSON.parse(localStorage.getItem("useAuth:expires_at")!)
        ).toBeGreaterThanOrEqual(currentTime + 500 * 1000);

        expect(localStorage.getItem("useAuth:user")).toEqual(
            JSON.stringify(action.user)
        );
    });

    it("sets user", () => {
        expect(authReducer(state, action).user).toEqual(action.user);
    });
    it("sets expiresAt", () => {
        expect(authReducer(state, action).expiresAt).toBeGreaterThanOrEqual(
            currentTime + 500 * 1000
        );
    });
    // ...
});
`}</code></pre>
    <p>{`Beautiful reducer tests. Take reducer as a function, pass-in a fake current state and an action, check for expected changes.`}</p>
    <p>{`Every part of the reducer's `}<inlineCode parentName="p">{`"login"`}</inlineCode>{` action gets tested.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`// src/authReducer.ts
switch (action.type) {
    case "login":
        const { authResult, user } = action;
        const expiresAt =
            authResult.expiresIn! * 1000 + new Date().getTime();

        if (typeof localStorage !== "undefined") {
            localStorage.setItem(
                "useAuth:expires_at",
                JSON.stringify(expiresAt)
            );
            localStorage.setItem("useAuth:user", JSON.stringify(user));
        }

        return {
            ...state,
            user,
            expiresAt,
            authResult
        };
`}</code></pre>
    <p>{`Can you spot the problem?`}</p>
    <h2 {...{
      "id": "testing-too-close-to-the-implementation"
    }}>{`Testing too close to the implementation`}</h2>
    <p>{`My tests are looking at implementation details. They know how the reducer works and test that way.`}</p>
    <p>{`That won't work for `}<a parentName="p" {...{
        "href": "https://xstate.js.org/"
      }}>{`XState`}</a>{`.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Yep, do you have a CodeSandbox (or Gist) of what you're trying to test?`}<br parentName="p"></br><br parentName="p"></br>{`Testing actors/machines is more of an "e2e" style than a unit-test style - you assert you get the right outcome after sending a sequence of events.`}</p>{`— David K. 🎹 (@DavidKPiano) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/DavidKPiano/status/1315420521530613765"
      }}>{`October 11, 2020`}</a></blockquote>
    <p>{`XState doesn't let you fake anything. You can't force it into a particular state, you can't trick it to do something it's not supposed to.`}</p>
    <p>{`That's the whole point. `}<em parentName="p">{`"Make impossible states impossible"`}</em>{` as the tagline goes.`}</p>
    <h2 {...{
      "id": "how-to-test-xstate"
    }}>{`How to test XState`}</h2>
    <p>{`As David says in his tweet, the trick is to go one level higher and write better tests. At the integration and API level.`}</p>
    <p>{`Start by initializing your state machine.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`// src/__tests__/authReducer.spec.ts

function initStateMachine() {
  const state = interpret(authMachine.withContext(initialContext)).start()

  return state
}
`}</code></pre>
    <p>{`You'll use this in multiple tests so having a function helps. It takes your state machine – `}<inlineCode parentName="p">{`authMachine`}</inlineCode>{` – seeds it with initial context and starts an `}<a parentName="p" {...{
        "href": "https://xstate.js.org/docs/guides/interpretation.html#interpreting-machines"
      }}>{`interpreter`}</a>{`.`}</p>
    <p><a parentName="p" {...{
        "href": "https://jestjs.io/"
      }}>{`Jest`}</a>{` runs tests within a `}<inlineCode parentName="p">{`describe`}</inlineCode>{` block in sequence`}<a parentName="p" {...{
        "href": "https://stackoverflow.com/questions/39831961/are-tests-inside-one-file-run-in-parallel-in-jest"
      }}>{`[`}{`1`}{`]`}</a>{`. That means you can share setup between tests.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`// init with values to easily tell TypeScript the type
let authState = initStateMachine(),
  savedContext = initialContext

beforeEach(() => {
  authState = initStateMachine()

  authState.subscribe((state) => {
    savedContext = state.context
  })
})
`}</code></pre>
    <p>{`Create a shared `}<inlineCode parentName="p">{`authState`}</inlineCode>{` and `}<inlineCode parentName="p">{`savedContext`}</inlineCode>{`, giving them values makes TypeScript's type inference kick in. Means you don't have to specify your own types.`}</p>
    <p>{`In a `}<inlineCode parentName="p">{`beforeEach`}</inlineCode>{` block you then instantiate a fresh state machine for each test and subscribe to changes. This lets you keep track of the latest application state.`}</p>
    <p>{`Each test then triggers the right sequence of actions and checks the result.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`// src/__tests__/authReducer.spec.ts
describe("LOGIN", // ...
    it("sets user", () => {
        authState.send("LOGIN");
        authState.send("AUTHENTICATED", loginPayload);

        expect(savedContext.user).toEqual(loginPayload.user);
    });
    it("sets expiresAt", () => {
        authState.send("LOGIN");
        authState.send("AUTHENTICATED", loginPayload);

        expect(savedContext.expiresAt).toBeGreaterThanOrEqual(
            currentTime + 500 * 1000
        );
    });
    it("sets authResult", () => {
        authState.send("LOGIN");
        authState.send("AUTHENTICATED", loginPayload);
        expect(savedContext.authResult).toBe(loginPayload.authResult);
    });
`}</code></pre>
    <p>{`We need 2 actions for each test:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`LOGIN`}</inlineCode>{` moves the state machine from `}<inlineCode parentName="li">{`unauthenticated`}</inlineCode>{` to `}<inlineCode parentName="li">{`authenticating`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`AUTHENTICATED`}</inlineCode>{` moves it from the `}<inlineCode parentName="li">{`authenticating`}</inlineCode>{` state into `}<inlineCode parentName="li">{`authenticated`}</inlineCode></li>
    </ul>
    <p>{`The state subscription runs every time and updates `}<inlineCode parentName="p">{`savedContext`}</inlineCode>{`. You can then verify the expected result.`}</p>
    <p>{`You could move action firing into `}<inlineCode parentName="p">{`beforeEach`}</inlineCode>{` to DRY this up further. I prefer to make it obvious what each test is testing.`}</p>
    <h2 {...{
      "id": "bonus-clean-side-effects"
    }}>{`Bonus: Clean side effects`}</h2>
    <p>{`Fun bonus from using XState 👉 clean, readable, predictable side-effects.`}</p>
    <p>{`Look at this `}<inlineCode parentName="p">{`login`}</inlineCode>{` action on the reducer:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`case "login":
    const { authResult, user } = action;
    const expiresAt =
        authResult.expiresIn! * 1000 + new Date().getTime();

    if (typeof localStorage !== "undefined") {
        localStorage.setItem(
            "useAuth:expires_at",
            JSON.stringify(expiresAt)
        );
        localStorage.setItem("useAuth:user", JSON.stringify(user));
    }

    return {
        ...state,
        user,
        expiresAt,
        authResult
    };
`}</code></pre>
    <p>{`Bleh. Deals with local storage, handles time, hard to read. You can't look at this and immediately know what's going on and why.`}</p>
    <p>{`Compare to the XState version:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`authenticated: {
    on: {
        LOGOUT: "unauthenticated"
    },
    entry: ["saveUserToContext", "saveToLocalStorage"],
    exit: ["clearUserFromContext", "clearLocalStorage"]
},
`}</code></pre>
    <p>{`Ah! When the user enters the `}<inlineCode parentName="p">{`authenticatd`}</inlineCode>{` state, we `}<inlineCode parentName="p">{`saveUserToContext`}</inlineCode>{` and `}<inlineCode parentName="p">{`saveToLocalStorage`}</inlineCode>{`. When they exit this state, we clear.`}</p>
    <p>{`Methods for that are defined later. Each does 1 thing.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-typescript"
      }}>{`actions: {
    saveUserToContext: assign((context, event) => {
        const { authResult, user } = event;
        const expiresAt =
            authResult.expiresIn! * 1000 + new Date().getTime();

        return {
            user,
            authResult,
            expiresAt
        };
    }),
    saveToLocalStorage: (context, event) => {
        const { expiresAt, user } = context;

        if (typeof localStorage !== "undefined") {
            localStorage.setItem(
                "useAuth:expires_at",
                JSON.stringify(expiresAt)
            );
            localStorage.setItem("useAuth:user", JSON.stringify(user));
        }
    },
`}</code></pre>
    <p>{`Same code as before but readable and organized. Beautiful 😍`}</p>
    <p>{`Cheers,`}<br />{`
~Swizec`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      